// ST-1258 -Claude- Light / Dark / System theme provider for nearrmeweb.
// Persists in localStorage. System mode follows OS color scheme.

import React, { createContext, useContext, useEffect, useState } from 'react';
import type { ColorScheme } from '@nearrme/theme';

interface ThemeContextValue {
  preference: ColorScheme;
  effective: 'light' | 'dark';
  setPreference: (pref: ColorScheme) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

const STORAGE_KEY = 'nearrme.theme';

function getOSScheme(): 'light' | 'dark' {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function loadPreference(): ColorScheme {
  if (typeof window === 'undefined') return 'system';
  const stored = localStorage.getItem(STORAGE_KEY) as ColorScheme | null;
  return stored ?? 'system';
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [preference, setPreferenceState] = useState<ColorScheme>(loadPreference);
  const [osScheme, setOsScheme] = useState<'light' | 'dark'>(getOSScheme);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => setOsScheme(e.matches ? 'dark' : 'light');
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const effective: 'light' | 'dark' = preference === 'system' ? osScheme : preference;

  useEffect(() => {
    document.documentElement.classList.toggle('dark', effective === 'dark');
  }, [effective]);

  const setPreference = (pref: ColorScheme) => {
    setPreferenceState(pref);
    localStorage.setItem(STORAGE_KEY, pref);
  };

  return (
    <ThemeContext.Provider value={{ preference, effective, setPreference }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used inside ThemeProvider');
  return ctx;
}
